<template>
  <section class="hero-banner" aria-labelledby="hero-banner-title">
    <!-- Video Background -->
    <div class="hero-banner__video-container" aria-hidden="true">
      <video ref="videoRef" class="hero-banner__video" autoplay muted loop playsinline :poster="data.video.poster">
        <source :src="data.video.url" type="video/mp4" />
      </video>
    </div>

    <!-- Content -->
    <div class="hero-banner__content">
      <!-- Back Navigation -->
      <router-link to="/" class="hero-banner__back">
        <span class="hero-banner__back-icon" aria-hidden="true">←</span>
        <span class="hero-banner__back-text">{{ t('viewSelfSellIn.hero_banner.back_button') }}</span>
      </router-link>

      <h1 id="hero-banner-title" class="hero-banner__title">
        <span class="hero-banner__title-main">{{ titleLeft }}</span>
        <span class="hero-banner__title-accent">{{ titleRight }}</span>
      </h1>

      <div class="hero-banner__bottom">
        <div>
          <div class="hero-banner__subtitle">
            <p v-if="subtitleLine1">{{ subtitleLine1 }}</p>
            <p v-if="subtitleLine2">{{ subtitleLine2 }}</p>
          </div>

          <div class="hero-banner__actions">
            <BackstageButton
              :variant="ButtonVariant.PRIMARY_INVERTED"
              :href="`#download-${SelfSellInCatalogDownloadType.SEASON}`">
              {{ t('viewSelfSellIn.hero_banner.season_catalog') }}
            </BackstageButton>
            <BackstageButton
              :variant="ButtonVariant.GHOST"
              :href="`#download-${SelfSellInCatalogDownloadType.OVERVIEW}`">
              {{ t('viewSelfSellIn.hero_banner.commercial_overview') }}
            </BackstageButton>
          </div>
        </div>

        <button
          class="hero-banner__mute-btn"
          :aria-label="isMuted ? 'Unmute background video' : 'Mute background video'"
          :aria-pressed="!isMuted"
          @click="toggleMute">
          <BackstageIcon :name="isMuted ? Icons.VOLUME_MUTED : Icons.VOLUME_ON" aria-hidden="true" />
        </button>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import BackstageButton from '@/components/common/BackstageButton.vue';
import BackstageIcon from '@/components/common/BackstageIcon.vue';
import { ButtonVariant } from '@/models/button-variant.enum';
import { Icons } from '@/models/icons.enum';
import { SelfSellInCatalogDownloadType } from '@onrunning/backstage-models';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useNewRelic } from '../../../composables/useNewRelic';
import type { ResolvedSelfSellInModel } from '../composables/useSelfSellIn';

const { t } = useI18n();
const newRelic = useNewRelic();

interface Props {
  data: ResolvedSelfSellInModel['heroBannerSection'];
}

const props = defineProps<Props>();

const titleParts = computed(() => props.data.title.split('/'));
const titleLeft = computed(() => titleParts.value[0]?.trim() ?? '');
const titleRight = computed(() => titleParts.value[1]?.trim() ?? '');

const descriptionParts = computed(() => props.data.description.split('/'));
const subtitleLine1 = computed(() => descriptionParts.value[0]?.trim() ?? '');
const subtitleLine2 = computed(() => descriptionParts.value[1]?.trim() ?? '');

const videoRef = ref<HTMLVideoElement | null>(null);
const isMuted = ref(true);

function toggleMute() {
  if (videoRef.value) {
    videoRef.value.muted = !videoRef.value.muted;
    isMuted.value = videoRef.value.muted;
  }
}

onMounted(() => {
  if (videoRef.value) {
    videoRef.value.play().catch((error) => {
      newRelic.noticeError(error, {
        component: 'HeroBanner',
      });
    });
  }
});
</script>

<style scoped lang="scss">
.hero-banner {
  position: relative;
  width: calc(100% + 48px);
  left: -24px;
  aspect-ratio: 16/9;
  overflow: hidden;

  &__video-container {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  &__video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__content {
    position: relative;
    width: 100%;
    height: 100%;
    padding: 34px 24px;
    color: white;
    display: grid;
    grid-template-rows: auto 1fr auto;
  }

  &__back {
    justify-self: start;
    display: flex;
    align-items: center;
    gap: 8px;
    mix-blend-mode: difference;
    color: var(--white-color);
    cursor: pointer;

    &-icon {
      font-size: 1.25rem;
      line-height: 1;
      color: var(--white-color);
    }

    &-text {
      font-weight: 400;
      font-size: 0.875rem;
      color: var(--white-color);
    }
  }

  &__title {
    display: flex;
    justify-content: space-between;
    align-items: center;
    align-self: center;
    font-weight: 700;
    font-size: 3.75rem;
    line-height: 1.25;
    letter-spacing: -1.2px;
    width: 100%;
  }

  &__subtitle {
    font-size: 0.875rem;
    margin-bottom: 18px;
    max-width: 300px;
    color: var(--white-color);
    text-align: left;
    line-height: 1.3;

    p {
      margin: 0;
    }
  }

  &__bottom {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
  }

  &__actions {
    display: flex;
    gap: 8px;
  }

  &__mute-btn {
    padding: 8px 18px;
    background: rgba(0, 0, 0, 0.5);
    border: none;
    border-radius: 100px;
    cursor: pointer;
    backdrop-filter: blur(12.5px);

    &:hover {
      background: rgba(0, 0, 0, 0.8);
    }
  }
}
</style>
